import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Modal } from '../../components/modal/Modal';
import { Table } from '../../components/table/Table';
import { TableColumn } from '../../components/table/types';
import { INVOICE_LIMIT, useFethInvoicesPage } from '../../hooks/useFetchInvoices';
import { InvoiceStateBadge } from '../../routes/_authenticated._main.invoicing.invoices/InvoiceStateBadge';
import { InvoiceV2DownloadButton } from '../../routes/_authenticated._main.invoicing.invoices/InvoiceV2DownloadButton';
import { getTextForKind } from '../../routes/_authenticated._main.invoicing.invoices/utils';
import { t } from '../../services/translation/translation';
import { formatDateForParisTZ } from '../../utils/date';
import { formatPrice } from '../../utils/price';

type Props = {
  onClose: () => void;
  selectedTransaction: string[];
};

export const TransactionInvoicesListModal = ({ onClose, selectedTransaction }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamKey = 'pageInvoices';
  const currentPage = Math.max(parseInt(searchParams.get(searchParamKey ? searchParamKey : 'page') ?? '1'), 1);

  const { data, isLoading, isFetching } = useFethInvoicesPage(
    {
      invoiceNumbers: selectedTransaction,
    },
    currentPage,
  );

  const invoices = useMemo(() => data?.items || [], [data?.items]);
  const nbPages = Math.ceil((data?.totalItems ?? 0) / INVOICE_LIMIT);

  const columns: TableColumn<(typeof invoices)[0]>[] = [
    {
      name: t('common.name'),
      render: invoice => (
        <div className="inline-flex flex-col items-start w-36">
          <span>{invoice.invoiceNumber}</span>
          <span className="text-sm opacity-50 font-normal">
            {formatDateForParisTZ(invoice.createdAt, t('date.date-short'), false)}
          </span>
        </div>
      ),
      sortKey: 'invoiceNumber',
    },
    {
      name: t('common.status'),
      render: invoice => <InvoiceStateBadge state={invoice.state} />,
      sortKey: 'state',
    },
    {
      name: t('common.type'),
      render: invoice => getTextForKind(invoice.invoiceKind),
      sortKey: 'invoiceKind',
    },
    {
      name: t('common.effective-payed-at'),
      render: invoice =>
        invoice.effectivePaymentDate
          ? formatDateForParisTZ(invoice.effectivePaymentDate, t('date.date-short'), false)
          : '--',
      sortKey: 'effectivePaymentDate',
    },
    {
      name: t('common.amount'),
      render: invoice => (
        <div className="inline-flex flex-col items-start w-36">
          {invoice.invoiceType === 'CREDIT_MEMO' && invoice.invoiceKind === 'DRIVER_TRIP_INVOICE' ? (
            <span className="font-mono text-red-600">
              {invoice.total.amountWithTax < 0
                ? formatPrice(invoice.total.amountWithTax)
                : `-${formatPrice(invoice.total.amountWithTax)}`}
            </span>
          ) : (
            <span>{formatPrice(invoice.total.amountWithTax)}</span>
          )}
        </div>
      ),
    },
    {
      name: '',
      render: invoice => <InvoiceV2DownloadButton invoice={invoice} />,
    },
  ];

  return (
    <Modal
      onClose={() => {
        setSearchParams(params => {
          params.delete(searchParamKey);
          return params;
        });
        onClose();
      }}
      className="max-w-6xl"
    >
      <Modal.Header
        onClose={() => {
          setSearchParams(params => {
            params.delete(searchParamKey);
            return params;
          });
          onClose();
        }}
        title={t('transaction.invoices-list-title')}
      />
      <Modal.Body>
        <div className="p-4 max-h-[80vh] overflow-y-auto">
          <Table
            identifier="id"
            columns={columns}
            data={invoices}
            isLoading={isLoading}
            isFetching={isFetching}
            context={undefined}
            totalItems={data?.totalItems ?? 0}
            nbPages={nbPages}
            searchParamKey={searchParamKey}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};
